import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { getCreditDebitNotes } from "../../services";
import {
  CreditDebitNoteFilterParams,
  CreditDebitNoteListRow,
} from "../../types/credit-debit-note.type";
import { formatNumber } from "../../utils";
import { formatOrderDate } from "../SpecialOrder/data";
import SpecialOrderPagination from "../SpecialOrder/Pagination";
import { NoteStatusChip, NoteTypeChip } from "./Chips";
import NoteEtaxStatus from "./NoteEtaxStatus";
import { creditDebitNoteDetailPath } from "./constants";

interface NoteTableProps {
  /** The list's filters, search already debounced; a new object only when a filter changes. */
  params: CreditDebitNoteFilterParams;
}

const COLUMN_COUNT = 8;
const DEFAULT_LIMIT = 10;
const SKELETON_ROWS = 5;

const headerCellSx = {
  borderRight: (theme: Theme) => `1px solid ${theme.palette.draft.main}`,
  whiteSpace: "nowrap",
};

const rowSx = { cursor: "pointer", "& td": { padding: "8px 12px" } };

function NoteRow({ row }: Readonly<{ row: CreditDebitNoteListRow }>) {
  const navigate = useNavigate();
  const handleClick = () => navigate(creditDebitNoteDetailPath(row.uuid));

  return (
    <TableRow hover onClick={handleClick} sx={rowSx}>
      <TableCell>
        <Typography variant="body2" fontWeight={600} color="primary">
          {row.note_no}
        </Typography>
      </TableCell>
      <TableCell>
        <NoteTypeChip noteType={row.note_type} />
      </TableCell>
      <TableCell>{formatOrderDate(row.note_date)}</TableCell>
      <TableCell>{row.original_invoice_no}</TableCell>
      <TableCell>
        <Typography variant="body2">{row.buyer_name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.customer_code ?? "-"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={600}>
          {formatNumber(row.grand_total)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <NoteStatusChip status={row.status} />
      </TableCell>
      <TableCell>
        <NoteEtaxStatus note={row} />
      </TableCell>
    </TableRow>
  );
}

export default function NoteTable({ params }: Readonly<NoteTableProps>) {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [rows, setRows] = useState<CreditDebitNoteListRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getCreditDebitNotes({ ...params, page, limit });
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
    } catch {
      // The interceptor already shows why the list could not be read.
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, params]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Any filter change starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [params]);

  const isEmpty = !loading && rows.length === 0;

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              <TableCell sx={headerCellSx}>เลขที่</TableCell>
              <TableCell sx={headerCellSx}>ประเภท</TableCell>
              <TableCell sx={headerCellSx}>วันที่</TableCell>
              <TableCell sx={headerCellSx}>ใบกำกับเดิม</TableCell>
              <TableCell sx={headerCellSx}>ลูกค้า</TableCell>
              <TableCell sx={headerCellSx} align="right">
                ยอดรวม
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                สถานะ
              </TableCell>
              <TableCell>e-Tax</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {Array.from({ length: COLUMN_COUNT }, (__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบเอกสารตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => <NoteRow key={row.uuid} row={row} />)}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack px={2} pt={1.5}>
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
      </Stack>
      <SpecialOrderPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
